import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import {
  readCurrentUserCommitEmail,
  setCurrentUserCommitEmail,
} from '@/lib/api/generated/api';
import type { UserCommitEmail } from '@/lib/api/generated/model';
import { expectStatus } from '@/lib/api/client';

import { commitEmailRefusal, commitEmailToSend } from '../commitIdentity';
import { COMMITS_COPY } from '../copy';
import { identityKeys } from '../queryKeys';

/** The service refused the address (422); `reason` is its explanation, if readable. */
export class CommitEmailRefused extends Error {
  readonly reason: string | null;

  constructor(reason: string | null) {
    super(reason ?? COMMITS_COPY.refused);
    this.name = 'CommitEmailRefused';
    this.reason = reason;
  }
}

/** Narrows a mutation's `unknown` error (constitution 2.5). */
export function isCommitEmailRefused(
  error: unknown,
): error is CommitEmailRefused {
  return error instanceof CommitEmailRefused;
}

/**
 * The signed-in person's commit email (spec 158): read it, save it, clear it.
 *
 * A save answers with what the service stored, which replaces the cache, so
 * the page's preview shows the stored address rather than the typed one. A
 * refusal is kept for the field to show beside itself; anything else is a toast,
 * because the field cannot help with a network or server failure.
 */
export function useCommitEmail() {
  const queryClient = useQueryClient();
  const key = identityKeys.commitEmail();
  const [refusal, setRefusal] = useState<CommitEmailRefused | null>(null);

  const query = useQuery({
    queryKey: key,
    queryFn: async ({ signal }): Promise<UserCommitEmail> =>
      expectStatus(
        await readCurrentUserCommitEmail({ signal }),
        200,
        COMMITS_COPY.loadFailed,
      ),
    retry: false,
  });

  const mutation = useMutation({
    mutationFn: async (
      commitEmail: string | null,
    ): Promise<UserCommitEmail> => {
      const response = await setCurrentUserCommitEmail({
        commit_email: commitEmail,
      });
      if (response.status === 200) return response.data;
      if (response.status === 422) {
        throw new CommitEmailRefused(commitEmailRefusal(response.data));
      }
      throw new Error(COMMITS_COPY.saveFailed);
    },
    onMutate: () => setRefusal(null),
    onSuccess: (stored) => {
      queryClient.setQueryData(key, stored);
      toast.success(
        stored.commit_email === null
          ? COMMITS_COPY.cleared
          : COMMITS_COPY.saved,
      );
    },
    onError: (error) => {
      if (isCommitEmailRefused(error)) {
        setRefusal(error);
        return;
      }
      toast.error(COMMITS_COPY.saveFailed);
    },
  });

  const saved = query.data?.commit_email ?? null;

  /** Save what the field holds; nothing is sent when it would change nothing. */
  function save(text: string) {
    if (mutation.isPending) return;
    const next = commitEmailToSend(text, saved);
    if (next === undefined) return;
    mutation.mutate(next);
  }

  /** Go back to the sign-in email. */
  function clear() {
    if (mutation.isPending || saved === null) return;
    mutation.mutate(null);
  }

  return {
    query,
    saved,
    save,
    clear,
    isSaving: mutation.isPending,
    refusal,
    resetRefusal: () => setRefusal(null),
  };
}
